import { theme } from 'aimo-ui';
import SkeletonLoaderRow, {
  SkeletonWrapper,
} from 'components/loading/SkeletonLoaderRow';
import { PermitElementRow } from 'components/services/PermitElementRow';
import AccountContext from 'context/AccountContext';
import useSubscriptions from 'hooks/useSubscriptions';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscriptionStore } from 'stores/subscriptionStore';
import { ItemWrapper, StyledWrapper } from 'styles/ContainerStyles';
import { SalesOrderStatus } from 'types/generatedSchemaTypes';
import { formatPermitValid } from 'utils/permitUtils';
import { AutoRenewalView } from '../permit/SalesOrderPermitCard';
import { useUiStore } from 'stores/uiStore';

interface SubscriptionDataProps {
  loading?: boolean;
}

const SubscriptionData: React.FC<SubscriptionDataProps> = ({ loading }) => {
  const { t } = useTranslation();
  const { isSubscriptionPaymentFailed } = useContext(AccountContext);
  const { subscription } = useSubscriptions();

  const { isRecurring, setIsRecurring } = useSubscriptionStore();

  const isTerminated = subscription?.status === 'TERMINATED';
  const failedOrTerminated = isSubscriptionPaymentFailed || isTerminated;

  const textColor = failedOrTerminated ? theme.colors.boost.red : undefined;

  const validFromText = () => {
    if (!subscription?.uid) {
      return t('longTermParking.validImmediately');
    }
    return formatPermitValid(subscription?.validFrom);
  };

  const onToggleRecurring = () => {
    if (isRecurring) {
      return useUiStore.getState().setSheetModal('cancelSubscription');
    }
    setIsRecurring(!isRecurring);
  };

  if (loading) {
    return (
      <StyledWrapper center={true} maxWidth={320} mb={0}>
        <ItemWrapper bgColor={theme.colors.transparentBlack}>
          <SkeletonWrapper width={100}>
            <SkeletonLoaderRow height={20} />
          </SkeletonWrapper>
          <SkeletonWrapper width={100}>
            <SkeletonLoaderRow height={20} />
          </SkeletonWrapper>
        </ItemWrapper>
        <ItemWrapper>
          <SkeletonWrapper width={100}>
            <SkeletonLoaderRow height={20} />
          </SkeletonWrapper>
          <SkeletonWrapper width={100}>
            <SkeletonLoaderRow height={20} />
          </SkeletonWrapper>
        </ItemWrapper>
      </StyledWrapper>
    );
  }

  return (
    <StyledWrapper center={true} maxWidth={320} mb={0}>
      <PermitElementRow
        label={t('permit.access.validFrom')}
        detail={validFromText()}
        borderColor={theme.colors.transparentBlack}
        withBottomBorder
      />
      {subscription?.uid && (
        <AutoRenewalView
          testID={'subscription-auto-renewal'}
          isRecurring={isRecurring}
          setIsRecurring={onToggleRecurring}
          status={subscription?.status as SalesOrderStatus}
          isInvoicing={false}
          allowRecurring={true}
          noBorder
        />
      )}
      {!subscription?.uid && (
        <PermitElementRow
          label={t('longTermParking.billingFrequency')}
          detail={t(
            `longTermParking.billingFrequencies.${subscription?.billingInterval}`
          )}
          borderColor={theme.colors.transparentBlack}
        />
      )}
      {subscription?.nextBillingDate && (
        <PermitElementRow
          label={
            subscription?.renewal
              ? t('services.nextBillingDate')
              : t('cityPermits.validUntil')
          }
          detail={formatPermitValid(subscription?.nextBillingDate)}
          labelColor={textColor}
          detailColor={textColor}
          withTopBorder
          borderColor={theme.colors.transparentBlack}
        />
      )}
      {isTerminated && (
        <PermitElementRow
          label={t('account.aimoSubscription.terminationDate')}
          detail={formatPermitValid(new Date(subscription?.endDate ?? ''))}
          labelColor={textColor}
          detailColor={textColor}
          withTopBorder
          borderColor={theme.colors.transparentBlack}
        />
      )}
    </StyledWrapper>
  );
};

export default SubscriptionData;
